import React, { useState, useEffect } from "react";
import Modal from "./Modal.tsx";
import { BasicDatePicker } from "../Forms/BasicDatePicker.js";
import { updateShift } from "../../services/shifts.js";
import { getStaffMembers } from "../../services/users.js";
import toast from "react-hot-toast";
import {
  formatUTCForDB,
  toUKWallTime,
  ukWallTimeToUTC,
} from "../../utils/functions.js";

export const EditShiftModal = ({ isOpen, onClose, shift, onSaved }) => {
  // Pickers default to the scheduled shift window when no clock time
  // was recorded, so backfilling starts from the likely correct values.
  // All picker values are UK wall time (Europe/London), independent of
  // the admin's device timezone.
  const [clockedIn, setClockedIn] = useState(
    () =>
      toUKWallTime(shift?.clockedIn || shift?.clockInTime) ||
      toUKWallTime(shift?.shiftStart || shift?.startTime)
  );
  const [clockedOut, setClockedOut] = useState(
    () =>
      toUKWallTime(shift?.clockedOut || shift?.clockOutTime) ||
      toUKWallTime(shift?.shiftEnd || shift?.endTime)
  );
  // The care-notes log is append-ordered, so the last entry is the latest
  // note. Pre-fill the field with it so an admin edits the current note
  // rather than starting from a blank box. Saving an unchanged note is
  // suppressed in handleSubmit so the append-only log never accumulates
  // duplicate entries.
  const latestCareNote =
    Array.isArray(shift?.careNotes) && shift.careNotes.length > 0
      ? shift.careNotes[shift.careNotes.length - 1]?.note ?? ""
      : "";
  const [careNote, setCareNote] = useState(latestCareNote);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // CCS-381: an admin may reassign the shift's staff member. Pre-select the
  // current assignee; the dropdown is populated from the staff directory.
  const originalStaffId = shift?.staff?.id ?? "";
  const [staffId, setStaffId] = useState(originalStaffId);
  const [staffOptions, setStaffOptions] = useState([]);

  // Load eligible staff for the dropdown. Guarded with an `active` flag so a
  // response that arrives after the modal closes/unmounts never sets state
  // (avoids the React "state update on an unmounted component" leak/warning).
  useEffect(() => {
    if (!isOpen) return undefined;
    let active = true;
    (async () => {
      try {
        const { data } = await getStaffMembers();
        if (active && Array.isArray(data)) {
          setStaffOptions(
            data.map((u) => ({
              value: u.staffId,
              label: `${u.firstName} ${u.lastName}`,
            }))
          );
        }
      } catch (err) {
        console.error(err);
      }
    })();
    return () => {
      active = false;
    };
  }, [isOpen]);

  // Originals for change detection — only edited fields are sent, so a
  // note-only save never rewrites clock times or flips the shift status.
  // Same UK-wall-time representation as the picker state so the dirty
  // check compares like with like.
  const originalClockedIn = toUKWallTime(
    shift?.clockedIn || shift?.clockInTime
  );
  const originalClockedOut = toUKWallTime(
    shift?.clockedOut || shift?.clockOutTime
  );
  const sameTime = (a, b) => (a?.getTime() ?? null) === (b?.getTime() ?? null);
  const staffChanged = staffId !== originalStaffId;

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const note = careNote.trim();
    const clockedInChanged = !sameTime(clockedIn, originalClockedIn);
    const clockedOutChanged = !sameTime(clockedOut, originalClockedOut);
    // Only a non-empty note that differs from the pre-filled current note is
    // a real change. This prevents re-appending an unchanged note and, since
    // notes are append-only, treats "blanked the field" as no-op rather than
    // an attempt to delete the existing note.
    const noteChanged = note.length > 0 && note !== latestCareNote.trim();

    if (!clockedInChanged && !clockedOutChanged && !noteChanged && !staffChanged) {
      setError("Nothing to update");
      return;
    }
    // CCS-381: a shift must always have a staff member assigned
    if (staffChanged && !staffId) {
      setError("A staff member is required");
      return;
    }
    if (clockedOut && !clockedIn) {
      setError("Clock out requires a clock in time");
      return;
    }
    if (clockedIn && clockedOut && clockedOut <= clockedIn) {
      setError("Clock out must be after clock in");
      return;
    }
    const now = new Date();
    if (
      (clockedInChanged && ukWallTimeToUTC(clockedIn) > now) ||
      (clockedOutChanged && ukWallTimeToUTC(clockedOut) > now)
    ) {
      setError("Clock times cannot be in the future");
      return;
    }
    setError("");

    // get-shift-details returns shiftId as a string; the update endpoint
    // requires a number
    const payload = { shiftId: Number(shift.shiftId) };
    if (clockedInChanged && clockedIn) {
      payload.clockedIn = formatUTCForDB(
        ukWallTimeToUTC(clockedIn),
        "yyyy-MM-dd HH:mm:ss"
      );
    }
    if (clockedOutChanged && clockedOut) {
      payload.clockedOut = formatUTCForDB(
        ukWallTimeToUTC(clockedOut),
        "yyyy-MM-dd HH:mm:ss"
      );
      payload.status = "completed";
    }
    if (noteChanged) {
      payload.careNotes = note;
    }
    if (staffChanged && staffId) {
      payload.staffId = staffId;
    }

    setIsSaving(true);
    try {
      await updateShift(payload);
      toast.success("Shift updated successfully");
      onSaved?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update shift");
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title="Edit shift record"
      size="md"
      onCancel={handleClose}
      isOpen={isOpen}
    >
      <form onSubmit={handleSubmit}>
        <div className="form__group">
          <label>Clock in</label>
          <BasicDatePicker
            showTimeInput
            height="48px"
            timeInputLabel="Time"
            value={clockedIn}
            onChange={setClockedIn}
            placeholder="Clock in time"
            dateFormat="yyyy-MM-dd HH:mm:ss"
            maxDate={toUKWallTime(new Date())}
          />
        </div>
        <div className="form__group">
          <label>Clock out</label>
          <BasicDatePicker
            showTimeInput
            height="48px"
            timeInputLabel="Time"
            value={clockedOut}
            onChange={setClockedOut}
            placeholder="Clock out time"
            dateFormat="yyyy-MM-dd HH:mm:ss"
            maxDate={toUKWallTime(new Date())}
          />
        </div>
        <div className="form__group">
          <label htmlFor="editShiftStaff">Assigned staff member</label>
          <div className="form__input">
            <select
              id="editShiftStaff"
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
            >
              {/* Keep the current assignee selectable even if they are not in
                  the returned directory (e.g. deactivated), so the field is
                  never unexpectedly blank. */}
              {originalStaffId &&
                shift?.staff &&
                !staffOptions.some((o) => o.value === originalStaffId) && (
                  <option value={originalStaffId}>
                    {`${shift.staff.firstName} ${shift.staff.lastName}`}
                  </option>
                )}
              {staffOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form__group">
          <label htmlFor="editShiftCareNote">Care note</label>
          <div className="form__input">
            <textarea
              id="editShiftCareNote"
              value={careNote}
              onChange={(e) => setCareNote(e.target.value)}
              placeholder="Add a care note (optional)"
            />
          </div>
        </div>
        {error && <span className="form__error">{error}</span>}
        <button
          type="submit"
          className="btn btn__primary btn--block"
          disabled={isSaving}
        >
          {isSaving ? "Loading..." : "Save changes"}
        </button>
      </form>
    </Modal>
  );
};
